import { FunctionComponent, useEffect, useState } from 'react'
import styles from './ReprintReportDialog.module.scss'
import Dialog from '../../_custom/Dialog/Dialog.tsx'
import DialogHeader from '../../_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '../../_custom/DialogBody/DialogBody.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '../../_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import DatePickerComponent from '../../_custom/Picker/datePicker/datePicker.tsx'
import { useMutation } from '@apollo/client'
import { REPRINT_DAYCARE_REPORT } from '@/modules/API/export/getExportData.ts'
import { Daycare } from '@/types/Daycare.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectMultiple from '../../_custom/Selector/selectMultiple/selectMultiple.tsx'
import moment, { Moment } from 'moment/moment'
import Checkbox from '../../_custom/Checkbox/Checkbox.tsx'
import { initMonthsTab, initYearsTab, Month, Year } from '@/container/settings/cron/initValueTab.tsx'
import SelectUnique from '../../_custom/Selector/selectUnique/selectUnique.tsx'
import Loading from '../../Loading/Loading.tsx'

interface ReprintReportDialogProps {
    open: boolean
    handleClose: () => void
    minDate: string
    daycares: Daycare[]
    fromGroup: boolean
}

const ReprintReportDialog: FunctionComponent<ReprintReportDialogProps> = ({
    open,
    handleClose,
    minDate,
    daycares,
    fromGroup
}: ReprintReportDialogProps) => {
    const { t } = useTranslation()

    const defaultDate: Moment = moment().subtract(1, 'month')

    const [startDate, setStartDate] = useState<string>(defaultDate.startOf('month').format('DD-MM-YYYY'))
    const [toDate, setToDate] = useState<string>(defaultDate.endOf('month').format('DD-MM-YYYY'))
    const [selectPeriod, setSelectPeriod] = useState<boolean>(false)

    const [selectedYear, setSelectedYear] = useState<string>(defaultDate.format('YYYY'))
    const [selectYear, setSelectYear] = useState<boolean>(false)

    const selectOptionsYear: SelectOptionsDeprecated = [
        {
            options: initYearsTab(moment(minDate, 'DD-MM-YYYY').year()).map((year: Year): SelectOptionDeprecated => {
                return {
                    name: year.years,
                    value: year.id
                }
            })
        }
    ]

    const [selectedMonth, setSelectedMonth] = useState<string>(defaultDate.format('MM-YYYY'))
    const selectOptionsMonth: SelectOptionsDeprecated = [
        {
            options: initMonthsTab(minDate).map((month: Month): SelectOptionDeprecated => {
                return {
                    name: month.months,
                    value: month.id
                }
            })
        }
    ]

    const [selectedDaycare, setSelectedDaycare] = useState<string[]>(
        daycares.map((daycare: Daycare): string => daycare.id.toString())
    )

    const daycareOptions: SelectOptionsDeprecated = [
        {
            options: daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                return {
                    name: daycare.name,
                    value: daycare.id.toString()
                }
            })
        }
    ]

    const [mailSent, setMailSent] = useState<boolean>(false)

    const [getReprintReportData, { loading: loadingExport, error: errorExport }] = useMutation(REPRINT_DAYCARE_REPORT, {
        variables: {
            daycareIds: selectedDaycare.map((id: string): number => Number(id)),
            from: selectPeriod
                ? startDate
                : selectYear
                  ? moment(selectedYear, 'YYYY').startOf('year').format('DD-MM-YYYY')
                  : moment(selectedMonth, 'MM-YYYY').startOf('month').format('DD-MM-YYYY'),
            to: selectPeriod
                ? toDate
                : selectYear
                  ? moment(selectedYear, 'YYYY').endOf('year').format('DD-MM-YYYY')
                  : moment(selectedMonth, 'MM-YYYY').endOf('month').format('DD-MM-YYYY'),
            fromGroup: fromGroup
        },
        onCompleted: () => {
            setMailSent(true)
        }
    })

    useEffect(() => {
        setSelectedDaycare(daycares.map((daycare: Daycare): string => daycare.id.toString()))
    }, [daycares])

    function handleCheckPeriod(check: boolean): void {
        if (check) setSelectYear(false)
        setSelectPeriod(check)
    }

    function handleCheckYear(check: boolean): void {
        if (check) setSelectPeriod(false)
        setSelectYear(check)
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('reprintReport')}></DialogHeader>
            <DialogBody>
                <div className={styles.DialogBody}>
                    {loadingExport ? (
                        <Loading overall={false} />
                    ) : errorExport ? (
                        <div className={styles.TextContainer}>
                            <p className={styles.Text}>{t('generalError')}</p>
                        </div>
                    ) : mailSent ? (
                        <div className={styles.TextContainer}>
                            <p className={styles.Text}>{t('reprintReportSuccess')}</p>
                        </div>
                    ) : (
                        <>
                            {daycares.length > 1 && (
                                <div className={styles.SelectContainer}>
                                    <SelectMultiple
                                        label={t('myDaycares')}
                                        selectOptions={daycareOptions}
                                        selectValues={selectedDaycare}
                                        setSelectValues={setSelectedDaycare}
                                        className={styles.UserInput}
                                        maxToShow={5}
                                    />
                                </div>
                            )}
                            {selectPeriod ? (
                                <div className={styles.SelectContainer}>
                                    <DatePickerComponent
                                        minDate={minDate}
                                        maxDate={toDate}
                                        value={startDate}
                                        setValue={setStartDate}
                                        label={t('fromDate')}
                                        className={styles.UserInput}
                                        disablePast={false}
                                    />
                                    <DatePickerComponent
                                        minDate={startDate}
                                        maxDate={moment().format('DD-MM-YYYY')}
                                        value={toDate}
                                        setValue={setToDate}
                                        label={t('toDate')}
                                        className={styles.UserInput}
                                        disablePast={false}
                                    />
                                </div>
                            ) : selectYear ? (
                                <div className={styles.SelectContainer}>
                                    <SelectUnique
                                        selectOptions={selectOptionsYear}
                                        selectValue={selectedYear}
                                        setSelectValue={setSelectedYear}
                                        label={t('year')}
                                        formClassName={styles.UserInput}
                                    />
                                </div>
                            ) : (
                                <div className={styles.SelectContainer}>
                                    <SelectUnique
                                        selectOptions={selectOptionsMonth}
                                        selectValue={selectedMonth}
                                        setSelectValue={setSelectedMonth}
                                        label={t('months')}
                                        formClassName={styles.UserInput}
                                    />
                                </div>
                            )}
                            <div className={styles.CheckBoxContainer}>
                                <Checkbox
                                    text={t('selectPeriod')}
                                    checked={selectPeriod}
                                    onChange={handleCheckPeriod}
                                    className={styles.CheckBox}
                                />
                                <Checkbox
                                    text={t('selectYear')}
                                    checked={selectYear}
                                    onChange={handleCheckYear}
                                    className={styles.CheckBox}
                                />
                            </div>
                        </>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={'export'}
                            onClick={getReprintReportData}
                            className={styles.Button}
                            disabled={loadingExport || errorExport !== undefined || mailSent}
                        />
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ReprintReportDialog
